import _ from 'underscore';
import React, { useCallback, useState, useRef } from 'react';

import { FolderIcon, FolderAssetType } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

import {
  colPreview, colId, colName, colPath, colType, colDeletionError, colMoveError, colAssociations, columnSizingOptions,
} from './columns';

import { SummaryResultModal } from './dialog';

const ASSET_TYPES = {
  [FolderAssetType.Image]: _TL_('Image'),
  [FolderAssetType.Video]: _TL_('Video'),
};

export const useSummaryResultModal = (i18n, logger, navigation, helpPopup) => {
  const [isModalVisible, setModalVisible] = useState(false);

  const titleRef = useRef('');
  const summaryRef = useRef('');
  const columnsRef = useRef([]);
  const itemsRef = useRef([]);

  const openDeleteSummaryResultModal = (title, deletedItems) => {
    const columns = [
      colPreview(i18n, helpPopup), colName(i18n, helpPopup), colPath(i18n, helpPopup), colType(i18n, helpPopup), colId(i18n, helpPopup),
      colDeletionError(i18n, helpPopup), colAssociations(i18n, navigation, logger, helpPopup),
    ];

    const items = _.chain(deletedItems)
      .filter(itm => itm.err)
      .map(({
        err, assetId, folderId, assetName, folderName, assetType, path, thumbnailUrl,
      }) => ({
        id: folderId || assetId,
        path,
        name: folderName || assetName,
        type: (folderId ? i18n.getString(_TL_('Folder')) : i18n.getString(ASSET_TYPES[assetType])) || '',
        errorMessage: i18n.getString(err.uxMessage),
        shortErrMsg: i18n.getString(err.shortMsg || _TL_('Error')),
        availableActions: err.availableActions || {},
        err,
        folderAssetType: assetType,
        thumbnailUrl,
        onRenderPreview: folderId ? () => (<FolderIcon />) : undefined,
      }))
      .value() || [];

    const failedAssetCount = items ? _.filter(items, itm => itm.folderAssetType).length : 0;
    const failedFolderCount = items ? _.filter(items, itm => !itm.folderAssetType).length : 0;
    if (failedFolderCount > 0 && failedAssetCount > 0) {
      const strings = [
        _TL_("{{failedAssetCount}} asset and {{failedFolderCount}} folder couldn't be deleted:"),
        _TL_("{{failedAssetCount}} asset and {{failedFolderCount}} folders couldn't be deleted:"),
        _TL_("{{failedAssetCount}} assets and {{failedFolderCount}} folder couldn't be deleted:"),
        _TL_("{{failedAssetCount}} assets and {{failedFolderCount}} folders couldn't be deleted:"),
      ];
      const strIdx = (Math.min(failedAssetCount - 1, 1) * 2) + Math.min(failedFolderCount - 1, 1);
      summaryRef.current = i18n.getString(strings[strIdx], { failedAssetCount, failedFolderCount });
    } else if (failedFolderCount > 1) {
      summaryRef.current = i18n.getString(_TL_("{{failedFolderCount}} folders couldn't be deleted:"), { failedFolderCount });
    } else if (failedFolderCount > 0) {
      summaryRef.current = i18n.getString(_TL_("1 folder couldn't be deleted:"));
    } else if (failedAssetCount > 1) {
      summaryRef.current = i18n.getString(_TL_("{{failedAssetCount}} assets couldn't be deleted:"), { failedAssetCount });
    } else {
      summaryRef.current = i18n.getString(_TL_("1 asset couldn't be deleted:"));
    }
    titleRef.current = title;
    itemsRef.current = items;
    columnsRef.current = columns;

    setModalVisible(true);
  };

  const openFolderDeleteSummaryResultModal = (deletedItems) => {
    openDeleteSummaryResultModal(i18n.getString(_TL_('Folder deletion errors')), deletedItems);
  };

  const openBatchDeleteSummaryResultModal = (deletedItems) => {
    openDeleteSummaryResultModal(i18n.getString(_TL_('Bulk deletion errors')), deletedItems);
  };

  const openBatchMoveSummaryResultModal = ({ assetsSummary, foldersSummary }) => {
    const columns = [
      colPreview(i18n, helpPopup), colName(i18n, helpPopup), colType(i18n, helpPopup), colId(i18n, helpPopup), colMoveError(i18n, helpPopup), colAssociations(i18n, navigation, logger, helpPopup),
    ];

    const assetErrors = _.chain(assetsSummary).filter(itm => itm.err).map(({
      err, assetId: id, assetName: name, folderAssetType, url,
    }) => ({
      id,
      name,
      type: i18n.getString(ASSET_TYPES[folderAssetType]) || '',
      errorMessage: i18n.getString(err.uxMessage),
      shortErrMsg: i18n.getString(err.shortMsg || _TL_('Error')),
      availableActions: err.availableActions || {},
      err,
      folderAssetType,
      thumbnailUrl: url,
    })).value() || [];
    const folderErrors = _.chain(foldersSummary).filter(itm => itm.err).map(({
      err, folderId: id, folderName: name,
    }) => ({
      id,
      name,
      type: i18n.getString(_TL_('Folder')),
      errorMessage: i18n.getString(err.uxMessage),
      shortErrMsg: i18n.getString(err.shortMsg || _TL_('Error')),
      availableActions: err.availableActions || {},
      err,
      folderAssetType: '',
      onRenderPreview: () => (<FolderIcon />),
    })).value() || [];

    if (assetErrors.length > 0 && folderErrors.length > 0) {
      const strings = [
        _TL_("{{failedAssetCount}} asset and {{failedFolderCount}} folder couldn't be moved:"),
        _TL_("{{failedAssetCount}} asset and {{failedFolderCount}} folders couldn't be moved:"),
        _TL_("{{failedAssetCount}} assets and {{failedFolderCount}} folder couldn't be moved:"),
        _TL_("{{failedAssetCount}} assets and {{failedFolderCount}} folders couldn't be moved:"),
      ];
      const strIdx = (Math.min(assetErrors.length - 1, 1) * 2) + Math.min(folderErrors.length - 1, 1);
      summaryRef.current = i18n.getString(strings[strIdx], { failedAssetCount: assetErrors.length, failedFolderCount: folderErrors.length });
    } else if (assetErrors.length > 1) {
      summaryRef.current = i18n.getString(_TL_("{{failedAssetCount}} assets couldn't be moved:"), { failedAssetCount: assetErrors.length });
    } else if (assetErrors.length > 0) {
      summaryRef.current = i18n.getString(_TL_("1 asset couldn't be moved:"));
    } else if (folderErrors.length > 1) {
      summaryRef.current = i18n.getString(_TL_("{{failedFolderCount}} folders couldn't be moved:"), { failedFolderCount: folderErrors.length });
    } else {
      summaryRef.current = i18n.getString(_TL_("1 folder couldn't be moved:"));
    }
    titleRef.current = i18n.getString(_TL_('Bulk move errors'));
    itemsRef.current = [...assetErrors, ...folderErrors];
    columnsRef.current = columns;

    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  const render = useCallback(() => (<SummaryResultModal
    show={isModalVisible}
    title={titleRef.current}
    summary={summaryRef.current}
    items={itemsRef.current}
    columns={columnsRef.current}
    columnSizingOptions={columnSizingOptions}
    onClose={closeModal}
    i18n={i18n}
    logger={logger}
  />), [i18n, isModalVisible, logger]);

  return {
    openFolderDeleteSummaryResultModal,
    openBatchDeleteSummaryResultModal,
    openBatchMoveSummaryResultModal,
    render,
  };
};
